import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Button, Card, CardContent, Grid, Typography } from '@mui/material';
import { useLottoTaxCalculator } from './LottoTaxCalculatorPage.hooks';
import styles from './LottoTaxCalculatorPage.module.css';
import { NumericInput } from '../../Input/NumericInput';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Helmet } from 'react-helmet';
import Adsense from '../../Ads/Adsense';

const LottoTaxCalculatorPage = () => {
  const { winningAmount, setWinningAmount, result, handleCalculate } =
    useLottoTaxCalculator();

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('ko-KR').format(num);
  };

  return (
    <div className={styles.container}>
      <Helmet>
        <title>로또 당첨금 세금 계산기 | Enzo's Toolkit</title>
        <meta
          name="description"
          content="로또 당첨금의 세금과 실수령액을 간편하게 계산해 보세요. 대한민국 세법 기준(200만원 이하 비과세, 3억원 이하 22%, 3억원 초과 33%)을 적용하여 정확한 계산 결과를 제공합니다."
        />
      </Helmet>
      <Typography variant="h4" component="h1" gutterBottom>
        로또 당첨금 세금 계산기
      </Typography>
      <Typography variant="body1" gutterBottom>
        당첨금을 입력하시면 세금과 실수령액을 계산해 드립니다. (대한민국 세법
        기준)
      </Typography>
      <Adsense adClient="ca-pub-9359050149154028" adSlot="1612216482" />
      <Accordion className={styles.taxInfoAccordion}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="tax-info-content"
          id="tax-info-header"
        >
          <Typography>세금 계산 방법 상세보기</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography component="div" variant="body2">
            <ul>
              <li>
                <strong>200만 원 이하:</strong> 비과세 (세금 없음)
              </li>
              <li>
                <strong>200만 원 초과 ~ 3억 원 이하:</strong> 총 당첨금액의 22%
                (소득세 20% + 주민세 2%)
              </li>
              <li>
                <strong>3억 원 초과:</strong> 3억 원까지는 22%, 3억 원을
                초과하는 금액에 대해서는 33% 적용 후 합산 (소득세 30% + 주민세
                3%)
              </li>
            </ul>
            <Typography variant="caption">
              * 복권 당첨금은 소득세법상 '기타소득'으로 분류되며, 분리과세
              대상입니다.
            </Typography>
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Grid
        container
        spacing={2}
        alignItems="flex-end"
        className={styles.inputSection}
      >
        <Grid item xs={12} sm={8}>
          <NumericInput
            label="총 당첨금 (원)"
            value={winningAmount}
            onValueChange={(values) => {
              setWinningAmount(values.floatValue || '');
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCalculate}
            fullWidth
            size="large"
          >
            계산하기
          </Button>
        </Grid>
      </Grid>

      {result && (
        <Card className={styles.resultCard}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom>
              계산 결과
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body1">총 당첨금:</Typography>
              </Grid>
              <Grid item xs={6} className={styles.resultValue}>
                <Typography variant="body1">
                  {formatNumber(result.totalWinnings)} 원
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body1" color="error">
                  세금 총액:
                </Typography>
              </Grid>
              <Grid item xs={6} className={styles.resultValue}>
                <Typography variant="body1" color="error">
                  - {formatNumber(result.taxAmount)} 원
                </Typography>
              </Grid>
              <Grid item xs={6} className={styles.resultDivider}>
                <hr />
              </Grid>
              <Grid item xs={6} className={styles.resultDivider}>
                <hr />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6">실수령액:</Typography>
              </Grid>
              <Grid item xs={6} className={styles.resultValue}>
                <Typography variant="h6">
                  {formatNumber(result.netWinnings)} 원
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <div className={styles.navigationButtonContainer}>
        <Button
          component={Link}
          to="/lotto"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
        >
          로또 번호 추천기로 돌아가기
        </Button>
      </div>
    </div>
  );
};

export default LottoTaxCalculatorPage;
